import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { apiCall } from "../Apis/apiCall";
import { handleApiResponse, handleCustomResponse } from "../utils/response";
import Shimmer from "../components/ui/Shimmer";
import CustomSelect from "../components/ui/CustomSelect";
import Breadcrumb from "../components/Breadcrumb";
import { CancelButtonForm, UpdateButtonForm } from "../components/ui/Button";

const EditTaxRatePage = () => {
  const [countries, setCountries] = useState([]);
  const [taxTypes, setTaxTypes] = useState([]);
  const [taxTypeId, setTaxTypeId] = useState("");
  const [taxPercent, setTaxPercent] = useState("");
  const [countryId, setCountryId] = useState("");
  const [tempCountryName, setTempCountryName] = useState("");
  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [taxLoading, setTaxLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const { taxId } = useParams();

  // Define active status options for CustomSelect
  const activeOptions = [
    { label: "Active", value: true },
    { label: "Inactive", value: false },
  ];

  // Fetch tax types
  const fetchTaxTypes = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        want_pagination: 0,
        sort_by: "name",
        sort_order: "asc",
      });

      const response = await apiCall(
        "get",
        `/get-all-tax-types?${queryParams.toString()}`,
        null,
        navigate,
        true
      );

      if (response?.data?.success) {
        const taxTypeList = response.data.data || [];
        setTaxTypes(taxTypeList);
        handleApiResponse(response.data, "success", "GET");
      } else {
        handleCustomResponse("error", "Failed to load tax types.");
        setErrorMessage("Failed to load tax types.");
      }
    } catch (error) {
      handleApiResponse(error.response?.data, "Err", "GET");
      setErrorMessage("Error loading tax types.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch countries
  const fetchCountries = async () => {
    try {
      setLoading(true);
      let allCountries = [];
      let currentPage = 1;
      let lastPage = 1;

      do {
        const queryParams = new URLSearchParams({
          want_pagination: 0,
          sort_by: "country_name",
          sort_order: "asc",
        });

        const response = await apiCall(
          "get",
          `/get-all-countries?${queryParams.toString()}`,
          null,
          navigate
        );

        if (response?.data?.success) {
          const countriesData = response.data.data || [];
          allCountries = [...allCountries, ...countriesData];
          lastPage = response.data.pagination?.last_page || 1;
          currentPage += 1;
          handleApiResponse(response.data, "success", "GET");
        } else {
          handleCustomResponse("error", "Failed to load countries.");
          setErrorMessage("Failed to load countries.");
          break;
        }
      } while (currentPage <= lastPage);

      setCountries(allCountries);
    } catch (error) {
      handleApiResponse(error.response?.data, "Err", "GET");
      setErrorMessage("Error loading countries.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch tax by ID
  const fetchTaxById = async (taxId) => {
    try {
      setTaxLoading(true);
      const response = await apiCall(
        "get",
        `/get-tax-by-id/${taxId}`,
        null,
        navigate
      );

      if (response?.data?.success) {
        const taxData = response.data.data || response.data.tax || null;
        if (taxData) {
          setTaxTypeId(taxData.tax_type_id || "");
          setTaxPercent(taxData.tax_percent || "");
          setTempCountryName(taxData.country_name || "");
          setActive(taxData.active !== undefined ? taxData.active : true);
        }
        handleApiResponse(response.data, "success", "GET");
      } else {
        handleCustomResponse("error", "Failed to fetch tax details.");
        setErrorMessage("Failed to fetch tax details.");
      }
    } catch (error) {
      handleApiResponse(error.response?.data, "Err", "GET");
      setErrorMessage("Error fetching tax details.");
    } finally {
      setTaxLoading(false);
    }
  };

  // Handle form submission
  const handleUpdateTax = async (e) => {
    e.preventDefault();
    if (!taxTypeId || !taxPercent || !countryId) {
      handleCustomResponse("error", "All fields are required.");
      setErrorMessage("All fields are required.");
      return;
    }
    if (isNaN(taxPercent) || taxPercent < 0) {
      handleCustomResponse("error", "Tax percentage must be a valid number.");
      setErrorMessage("Tax percentage must be a valid number.");
      return;
    }
    if (isNaN(countryId) || countryId <= 0) {
      handleCustomResponse("error", "Country ID must be a valid number.");
      setErrorMessage("Country ID must be a valid number.");
      return;
    }
    if (isNaN(taxTypeId) || taxTypeId <= 0) {
      handleCustomResponse("error", "Tax Type ID must be a valid number.");
      setErrorMessage("Tax Type ID must be a valid number.");
      return;
    }
    if (!taxId || isNaN(taxId)) {
      handleCustomResponse("error", "Invalid or missing Tax ID.");
      setErrorMessage("Invalid or missing Tax ID.");
      return;
    }

    try {
      setSaveLoading(true);
      setErrorMessage("");
      const response = await apiCall(
        "put",
        `/update-tax/${taxId}`,
        {
          tax_type_id: parseInt(taxTypeId),
          tax_percent: parseFloat(taxPercent),
          country_id: parseInt(countryId),
          active,
        },
        navigate
      );

      if (response?.data?.success) {
        handleApiResponse(response.data, "success", "PUT");
        setTimeout(() => {
          navigate("/tax-rates");
        }, 1000);
      } else {
        handleCustomResponse("error", "Failed to update tax.");
        setErrorMessage("Failed to update tax.");
      }
    } catch (error) {
      handleApiResponse(error.response?.data, "Err", "PUT");
      setErrorMessage("Error updating tax.");
    } finally {
      setSaveLoading(false);
    }
  };

  // Check if form is valid
  const isFormValid = () => {
    return taxTypeId && taxPercent && countryId && !isNaN(taxPercent) && taxPercent >= 0;
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchTaxTypes();
    fetchCountries();
    if (taxId) {
      fetchTaxById(taxId);
    }
  }, [taxId]);

  // Set country when countries are loaded
  useEffect(() => {
    if (tempCountryName && countries.length > 0) {
      const matchedCountry = countries.find(
        (country) => country.country_name === tempCountryName
      );
      if (matchedCountry) {
        setCountryId(matchedCountry.country_id);
      }
    }
  }, [countries, tempCountryName]);

  return (
    <>
      <Breadcrumb title="Edit Tax" />

      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          {errorMessage && (
            <div
              className="alert alert-danger alert-dismissible fade show mb-3"
              role="alert"
            >
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
              ></button>
            </div>
          )}

          {loading || taxLoading ? (
            <Shimmer type="taxRateModal" />
          ) : (
            <form onSubmit={handleUpdateTax} autoComplete="off">
              <div className="row g-20">
                <div className="col-md-6 mb-20">
                  <label htmlFor="countryId" className="form-label">
                    Country *
                  </label>
                  <CustomSelect
                    name="countryId"
                    value={countryId}
                    onChange={(selected) => setCountryId(selected?.value || "")}
                    options={countries}
                    placeholder="Select Country"
                    labelKey="country_name"
                    valueKey="country_id"
                    isSearchable={true}
                    isClearable={true}
                    isDisabled={loading || saveLoading || taxLoading}
                  />
                </div>
                <div className="col-md-6 mb-20">
                  <label htmlFor="taxTypeId" className="form-label">
                    Tax Type *
                  </label>
                  <CustomSelect
                    name="taxTypeId"
                    value={taxTypeId}
                    onChange={(selected) => setTaxTypeId(selected?.value || "")}
                    options={taxTypes}
                    placeholder="Select Tax Type"
                    labelKey="name"
                    valueKey="id"
                    isSearchable={true}
                    isClearable={true}
                    isDisabled={loading || saveLoading || taxLoading}
                  />
                </div>
                <div className="col-md-6 mb-20">
                  <label htmlFor="taxPercent" className="form-label">
                    Tax Percent (%) *
                  </label>
                  <input
                    type="number"
                    className="form-control radius-8"
                    id="taxPercent"
                    value={taxPercent}
                    onChange={(e) => setTaxPercent(e.target.value)}
                    placeholder="Enter tax percent"
                    min="0"
                    step="0.01"
                    disabled={loading || saveLoading || taxLoading}
                  />
                </div>
                <div className="col-md-6 mb-20">
                  <label htmlFor="active" className="form-label">
                    Active *
                  </label>
                  <CustomSelect
                    name="active"
                    value={active}
                    onChange={(selected) => setActive(selected?.value ?? true)}
                    options={activeOptions}
                    placeholder="Select Status"
                    labelKey="label"
                    valueKey="value"
                    isSearchable={false}
                    isClearable={false}
                    isDisabled={loading || saveLoading || taxLoading}
                  />
                </div>
              </div>

              <div className="d-flex justify-content-center gap-3">
                <CancelButtonForm onClick={() => navigate("/tax-rates")} />
                <UpdateButtonForm
                  disabled={saveLoading || !isFormValid()}
                  text={saveLoading ? "Please wait..." : "Update Tax"}
                />
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default EditTaxRatePage;